import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseScopeChangeReason,
  ReleaseTransitionView,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { REASON_REQUIRING_COMMENT, SCOPE_CHANGE_REASONS } from "./releaseVocabulary";

/**
 * Performing a named act on a release (§7).
 *
 * <p>Shows what the move will do before doing it. The consequence text comes from the server
 * alongside the transition, so the sentence a person reads is the one the workflow actually
 * carries — not a second copy in the browser that drifts the first time the rules change.
 *
 * <p>Moves that interrupt or undo ask for a coded reason, and "Something else" asks for a
 * sentence. The server refuses both without them; asking here means somebody finds out before
 * they have committed to the action rather than after.
 */
export function ReleaseTransitionDrawer({
  release,
  transition,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  transition: ReleaseTransitionView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState<ReleaseScopeChangeReason | "">("");
  const [comment, setComment] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (transition) {
      setReason("");
      setComment("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition]);

  if (!transition) {
    return null;
  }

  const needsComment = reason !== "" && REASON_REQUIRING_COMMENT.includes(reason);
  const canConfirm =
    !transition.requiresReason || (reason !== "" && (!needsComment || comment.trim() !== ""));

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/transitions`, {
          transition: transition.transition,
          reason: reason === "" ? null : reason,
          comment: comment.trim() === "" ? null : comment.trim(),
          lockVersion: release.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title={transition.label}
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canConfirm} loading={saving}>
            {saving ? "Working…" : transition.label}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <div className="rounded-lg border-l-4 border-brand-500 border border-brand-100 bg-brand-50 px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
            What this does
          </p>
          <p className="mt-1 text-sm text-ink-800">{transition.consequence}</p>
        </div>

        {transition.requiresReason && (
          <>
            <Field label="Reason" required>
              <Select
                value={reason}
                onChange={(event) =>
                  setReason(event.target.value as ReleaseScopeChangeReason | "")
                }
              >
                <option value="">Choose a reason…</option>
                {SCOPE_CHANGE_REASONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="What happened"
              required={needsComment}
              hint={
                needsComment
                  ? '"Something else" counts as nothing on its own — say what it was.'
                  : "Optional, and kept with the record."
              }
            >
              <Textarea
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
